import { format } from "date-fns";
import { Typography } from "@/components/atoms";
import { driverDelayCaption } from "@/helpers/bookingHelpers";
import { useAppSelector } from "@/redux/hooks";
import { transferRateKind } from "@/redux/slices/clientBooking/bookingPayload";
import {
  selectBookedForDuration,
  selectBookingExtras,
  selectBookingTransferType,
  selectBookingVehicle,
  selectJobRate,
  selectNumOfBags,
  selectNumOfPassengers,
  selectPickupDatetime,
  selectPickupFlightDelayMinutes,
  selectPickupFlightNumber,
} from "@/redux/slices/clientBooking/selectors";
import { FlightStatusBadge, flightStatusBadgeShows } from "../FlightStatusBadge";
import { useBookedFlightStatus } from "../hooks/useBookedFlightStatus";
import { capacityLabel, extrasLabel, formatDuration } from "../vehicleTransfer";
import { DetailRow } from "./DetailRow";
import { PassengerRow } from "./PassengerRow";
import { TripVehicleRow } from "./TripVehicleRow";

/**
 * The passenger / date / vehicle rows of the Trip Details box, read live from the booking draft.
 * Shared by both Booking Summary variants (draft steps and the priced Payment step) so the section
 * reads identically everywhere. Each row renders only when its data exists — no em-dash placeholders.
 * The vehicle row carries three lines (transfer type, vehicle + capacity, extras), so it's laid out
 * inline here rather than through the two-line DetailRow.
 */
export const TripCoreRows = () => {
  const pickupDatetime = useAppSelector(selectPickupDatetime);
  const pickupDate = pickupDatetime ? new Date(pickupDatetime) : null;
  const hasValidDate = !!pickupDate && !Number.isNaN(pickupDate.getTime());
  const vehicle = useAppSelector(selectBookingVehicle);
  const transferType = useAppSelector(selectBookingTransferType);
  const numOfPassengers = useAppSelector(selectNumOfPassengers);
  const numOfBags = useAppSelector(selectNumOfBags);
  const vehicleExtras = extrasLabel(useAppSelector(selectBookingExtras));
  // Duration only applies to hourly/daily bookings. Gate on `jobRate` — the authoritative rate set in
  // every flow (create sets it from the transfer type; view/edit hydrate it from the booking) — since
  // the hydrated transferType doesn't carry `type`.
  const jobRate = useAppSelector(selectJobRate);
  const bookedForDuration = useAppSelector(selectBookedForDuration);
  const durationLabel = transferRateKind(jobRate) !== "journey" ? formatDuration(bookedForDuration) : "";
  // Hydrated from the booking on the view-only replay (viewDraft: flight_pickup_delay_minutes), so this
  // is how an existing booking surfaces the wait. Still gated on the flight number — the wait is a
  // flight concept and the payload only ever sends it alongside one.
  //
  // It rides on the date row as a trailing caption rather than in a row of its own, because the caption
  // has to be bound to the pickup time to mean anything: it says the time beside it ALREADY includes
  // the wait, which a separate row could never say.
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const delayCaption = driverDelayCaption(useAppSelector(selectPickupFlightDelayMinutes));

  // The flight's live status, beside the date it is pinned to (RM-Client 7039-22364). This is the one place
  // in the summary where the flight is already the subject, so a reader who never opens the Date & Time
  // step still learns that the arrival has moved — which is the whole complaint on DSN-641.
  //
  // The badge, not the reason: the footer on the flight card owns the arithmetic. Here it is a signal that
  // there is something to go and read.
  const flightStatus = useBookedFlightStatus();
  // Asked BEFORE laying out, not just left to the badge to withhold: `DetailRow` only emits its horizontal
  // trailing wrapper when something is passed to it, and a wrapper holding a badge that renders null is an
  // empty flex child still drawing the column's 2px gap.
  const showFlightBadge = !!flightStatus && flightStatusBadgeShows(flightStatus.status, flightStatus.delay);

  return (
    <>
      <PassengerRow />
      {hasValidDate && pickupDate && (
        <DetailRow
          icon="Calendar"
          title={format(pickupDate, "d MMMM yyyy")}
          subtitle={format(pickupDate, "h:mm a")}
          subtitleTrailing={
            (pickupFlightNumber && delayCaption) || showFlightBadge ? (
              <>
                {pickupFlightNumber && delayCaption && (
                  <Typography variant="caption" className="whitespace-nowrap text-info">
                    {delayCaption}
                  </Typography>
                )}
                {showFlightBadge && <FlightStatusBadge status={flightStatus!.status} delay={flightStatus!.delay} size="sm" />}
              </>
            ) : undefined
          }
        />
      )}
      {vehicle && (
        <TripVehicleRow
          transferLabel={transferType?.label ?? "Vehicle and Transfer"}
          vehicleType={`${vehicle.name} (${capacityLabel(numOfPassengers ?? 1, numOfBags ?? 0)})`}
          duration={durationLabel || undefined}
          extras={vehicleExtras || undefined}
        />
      )}
    </>
  );
};
